import PropTypes from 'prop-types';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import useSWR from 'swr';

// Utils
import { get } from '@/utils/client';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Loader from '@/components/base/Loader';
import InfoBanner from '@/components/base/InfoBanner';

const MobilePreview = ({
    className,
    children,
    apiEndpoint,
    socialProfile,
    wrapperHeight,
    additionalHeight = 85
}) => {
    const { t } = useTranslation();

    const { data: profile, error: profileError } = useSWR(
        apiEndpoint,
        (url) => get(url).then((res) => res.data),
        {
            shouldRetryOnError: false
        }
    );

    return (
        <div className={clsx(className, 'h-[720px] w-[384px] w-full')}>
            <div className="h-full rounded-[60px] border-3 border-white bg-white/40 p-3.5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack">
                <div className="relative h-[685px] overflow-y-scroll rounded-[46px] border-2 border-white bg-white no-scrollbar dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-anthracite">
                    <div
                        className="absolute inset-0 overflow-y-scroll no-scrollbar"
                        style={{ height: `${wrapperHeight - additionalHeight - 14}px` }}>
                        <div className="p-8">
                            {profileError ? (
                                <InfoBanner
                                    title={t('commons:error')}
                                    message={t('errors:error_occured_fetch_data')}
                                    className="mb-5 w-full"
                                />
                            ) : null}
                            <div className="flex items-center justify-center space-x-4">
                                <Avatar
                                    url={socialProfile?.external_api_data?.avatar}
                                    fallback={socialProfile?.slug?.[0]}
                                    size="lg"
                                />
                                <div className="flex flex-1 space-x-2">
                                    <div className="flex-1 text-center">
                                        <p className="text-sm">
                                            {profile ? (
                                                profile?.media_count
                                            ) : (
                                                <Loader className="inline-block h-3 w-3 text-icoBlue" />
                                            )}
                                        </p>
                                        <p className="text-xs text-icoGray-500">
                                            {t('scheduler:feed.posts')}
                                        </p>
                                    </div>
                                    <div className="flex-1 text-center">
                                        <p className="text-sm">
                                            {profile ? (
                                                profile?.followers_count
                                            ) : (
                                                <Loader className="inline-block h-3 w-3 text-icoBlue" />
                                            )}
                                        </p>
                                        <p className="text-xs text-icoGray-500">
                                            {t('scheduler:feed.followers')}
                                        </p>
                                    </div>
                                    <div className="flex-1 text-center">
                                        <p className="text-sm">
                                            {profile ? (
                                                profile?.follows_count
                                            ) : (
                                                <Loader className="inline-block h-3 w-3 text-icoBlue" />
                                            )}
                                        </p>
                                        <p className="text-xs text-icoGray-500">
                                            {t('scheduler:feed.followings')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {socialProfile?.external_api_data?.name ? (
                                <p className="mt-5 font-GBold text-sm">
                                    {socialProfile.external_api_data.name}
                                </p>
                            ) : null}
                            {profile ? (
                                profile.bio ? (
                                    <div className="mt-2 flex flex-col font-GMedium text-xs">
                                        <p>{profile.bio}</p>
                                        {profile?.website ? (
                                            <a
                                                className="max-w-min font-GMedium text-xs text-icoBlue"
                                                href={profile?.website}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                {profile?.website}
                                            </a>
                                        ) : null}
                                    </div>
                                ) : null
                            ) : (
                                <span className="mt-2 block w-full animate-pulse rounded bg-icoGray-300 py-1.5 dark:bg-icoDarkMode-deepBlack"></span>
                            )}
                        </div>
                        <div className="pb-2">{children}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

MobilePreview.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
    apiEndpoint: PropTypes.string,
    socialProfile: PropTypes.object,
    wrapperHeight: PropTypes.number,
    additionalHeight: PropTypes.number
};

export default MobilePreview;
